import { useState } from "react";
import { Barbell, Lightning, Stack, CaretDown, CaretRight, Info, X } from "@phosphor-icons/react";
import { SharedWorkoutCard } from "./SharedWorkoutCard";

function ProgramDescriptionModal({ title, description, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      data-testid="program-description-modal"
    >
      <button type="button" aria-label="Chiudi" onClick={onClose} className="absolute inset-0 bg-black/75 backdrop-blur-sm cursor-default" />
      <div className="relative w-full max-w-md max-h-[80vh] bg-[#0F0F0F] border border-[#007AFF]/40 rounded-2xl flex flex-col overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#1A1A1A] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#007AFF]">Descrizione programma</p>
            <h3
              className="text-base font-bold tracking-tight text-white uppercase mt-0.5 truncate"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#1A1A1A] hover:bg-[#262626] text-zinc-400 hover:text-white flex items-center justify-center flex-shrink-0"
            aria-label="Chiudi"
            data-testid="program-description-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">
          <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
            {description || "Nessuna descrizione."}
          </p>
        </div>
      </div>
    </div>
  );
}

function ProgramSection({ program, workouts, phases, token, logs, onLogSaved, exerciseLibrary, clientName }) {
  const phase = phases.find((p) => p.id === program.phase_id);
  const phaseTitle = phase?.title;
  const phaseCompleted = phase?.status === "completata";
  const programCompleted = program?.status === "completata";
  // A program is "pending" only if it has explicit dates AND auto-status says it hasn't started yet
  const hasDates = !!(program?.start_date && program?.end_date);
  const programPending = hasDates && program?.status === "da_fare";
  const completed = programCompleted || phaseCompleted;
  const dimmed = completed || programPending;
  const [open, setOpen] = useState(!dimmed);
  const [descOpen, setDescOpen] = useState(false);
  const programWorkouts = workouts.filter((w) => w.program_id === program.id);
  const hasDescription = !!(program.description && program.description.trim());
  return (
    <div
      className={`rounded-xl overflow-hidden transition-all ${
        dimmed
          ? "bg-[#0A0A0A] border border-[#1A1A1A] opacity-50"
          : "bg-[#0F0F0F] border border-[#262626]"
      }`}
      data-testid={`shared-program-${program.id}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-4 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            dimmed
              ? "bg-zinc-800/50 border border-zinc-700/30"
              : "bg-gradient-to-br from-[#007AFF]/20 to-[#007AFF]/5 border border-[#007AFF]/20"
          }`}>
            <Stack size={18} weight="duotone" className={dimmed ? "text-zinc-600" : "text-[#007AFF]"} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <h3 className={`font-bold text-sm tracking-tight truncate ${dimmed ? "text-zinc-500" : "text-white"}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {program.title.toUpperCase()}
              </h3>
              {hasDescription && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setDescOpen(true); }}
                  aria-label="Mostra descrizione programma"
                  className={`p-0.5 rounded transition-colors flex-shrink-0 ${dimmed ? "text-zinc-600 hover:text-zinc-400" : "text-zinc-500 hover:text-[#007AFF]"}`}
                  data-testid={`shared-program-info-${program.id}`}
                >
                  <Info size={14} weight="regular" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {phaseTitle && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 ${
                  dimmed
                    ? "bg-[#34C759]/10 text-[#34C759]/70"
                    : "bg-[#D4FF00]/10 text-[#D4FF00]"
                }`}>
                  <Lightning size={8} weight="fill" />{phaseTitle}
                </span>
              )}
              {completed && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-[#34C759]/10 text-[#34C759]/70">
                  completata
                </span>
              )}
              {programPending && !completed && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-zinc-700/40 text-zinc-400">
                  in attesa
                </span>
              )}
              <span className={`text-[10px] ${dimmed ? "text-zinc-700" : "text-zinc-600"}`}>{programWorkouts.length} schede</span>
            </div>
          </div>
        </div>
        {open ? <CaretDown size={16} className="text-zinc-500 flex-shrink-0" /> : <CaretRight size={16} className="text-zinc-500 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {programWorkouts.length > 0 ? (
            programWorkouts.map((w) => <SharedWorkoutCard key={w.id} workout={w} token={token} logs={logs} onLogSaved={onLogSaved} exerciseLibrary={exerciseLibrary} clientName={clientName} />)
          ) : (
            <p className="text-center py-4 text-zinc-600 text-xs">Nessuna scheda</p>
          )}
        </div>
      )}
      {descOpen && (
        <ProgramDescriptionModal
          title={program.title}
          description={program.description}
          onClose={() => setDescOpen(false)}
        />
      )}
    </div>
  );
}

export function WorkoutsView({ programs, workouts, phases, token, logs, onLogSaved, exerciseLibrary, clientName }) {
  const unassigned = workouts.filter((w) => !w.program_id);
  if (programs.length === 0 && workouts.length === 0) {
    return (
      <div className="text-center py-12 text-zinc-500 text-sm">
        <Barbell size={36} weight="duotone" className="mx-auto mb-2 text-zinc-600" />
        Nessun programma o scheda disponibile
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {programs.map((p) => (
        <ProgramSection key={p.id} program={p} workouts={workouts} phases={phases} token={token} logs={logs} onLogSaved={onLogSaved} exerciseLibrary={exerciseLibrary} clientName={clientName} />
      ))}
      {unassigned.length > 0 && (
        <div>
          {programs.length > 0 && (
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600 mb-2 px-1">
              Schede senza programma
            </p>
          )}
          <div className="space-y-2">
            {unassigned.map((w) => <SharedWorkoutCard key={w.id} workout={w} token={token} logs={logs} onLogSaved={onLogSaved} exerciseLibrary={exerciseLibrary} clientName={clientName} />)}
          </div>
        </div>
      )}
    </div>
  );
}
